import { DeviceHandle } from '../types'

// The WebUSB calls used here; the DOM lib ships no WebUSB types.
interface UsbEndpoint { direction: 'in' | 'out'; endpointNumber: number }
export interface UsbDevice {
  opened: boolean
  vendorId: number
  productId: number
  serialNumber?: string
  productName?: string
  manufacturerName?: string
  configuration: { interfaces: { interfaceNumber: number; alternate: { endpoints: UsbEndpoint[] } }[] } | null
  open: () => Promise<void>
  selectConfiguration: (n: number) => Promise<void>
  claimInterface: (n: number) => Promise<void>
  transferOut: (endpoint: number, data: BufferSource) => Promise<unknown>
}
interface Usb {
  requestDevice: (options: { filters: object[] }) => Promise<UsbDevice>
  getDevices: () => Promise<UsbDevice[]>
  addEventListener?: (type: 'connect' | 'disconnect', cb: (e: { device: UsbDevice }) => void) => void
  removeEventListener?: (type: 'connect' | 'disconnect', cb: (e: { device: UsbDevice }) => void) => void
}

export const webUsb = (): Usb | undefined => (typeof navigator === 'undefined' ? undefined : (navigator as unknown as { usb?: Usb }).usb)

const claimed = new WeakMap<UsbDevice, number>()

const claim = async (d: UsbDevice) => {
  if (claimed.has(d) && d.opened) return claimed.get(d)!
  if (!d.opened) await d.open()
  if (!d.configuration) await d.selectConfiguration(1)
  const iface = d.configuration?.interfaces.find((i) => i.alternate.endpoints.some((e) => e.direction === 'out'))
  const out = iface?.alternate.endpoints.find((e) => e.direction === 'out')
  if (!iface || !out) throw new Error('This USB device has no output endpoint')
  await d.claimInterface(iface.interfaceNumber)
  claimed.set(d, out.endpointNumber)
  return out.endpointNumber
}

export const usbHandle = (d: UsbDevice): NonNullable<DeviceHandle['usb']> => ({
  vendor_id: d.vendorId,
  product_id: d.productId,
  ...(d.serialNumber ? { serial_number: d.serialNumber } : {}),
})

/** The browser's device picker, filtered to known vendors (falls back to every device). Needs a click. */
export const pickUsbDevice = async (vendorIds: number[] = []) => {
  const usb = webUsb()
  if (!usb) throw new Error('This browser cannot reach USB devices; use Chrome or Edge')
  return usb.requestDevice({ filters: vendorIds.map((vendorId) => ({ vendorId })) })
}

const findUsb = async (h: NonNullable<DeviceHandle['usb']>) => {
  const all = (await webUsb()?.getDevices()) ?? []
  const device = all.find(
    (d) => d.vendorId === h.vendor_id && d.productId === h.product_id && (!h.serial_number || d.serialNumber === h.serial_number)
  )
  if (!device) throw new Error('The USB device is not connected (or this browser has not been allowed to use it)')
  return device
}

export const sendUsb = async (h: NonNullable<DeviceHandle['usb']>, data: Uint8Array) => {
  const device = await findUsb(h)
  const endpoint = await claim(device)
  // Large jobs in chunks; some printers drop oversized transfers.
  for (let i = 0; i < data.length; i += 16_384) await device.transferOut(endpoint, data.slice(i, i + 16_384))
}
